import React, { useEffect, useState } from 'react';
import { Save, Plus, Trash2, GripVertical } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ImageUploader from '../../components/admin/ImageUploader';

const EMPTY_SETTINGS = {
  heroSlides: [],
  aboutImage: '',
  hoursImage: '',
  whyImage: '',
  signatureBgImage: '',
  galleryImages: [],
  aboutHero: '',
  faqsHero: '',
  contactHero: '',
  franchisingHero: '',
};

const EMPTY_SLIDE = { tag: '', emoji: '', title: '', subtitle: '', cta: 'Order Online', ctaTo: '/menu', image: '', startAt: '', endAt: '' };

export default function AdminSettings() {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get('/site-settings').then((r) => setSettings({ ...EMPTY_SETTINGS, ...r.data }));
  }, []);

  const setField = (k) => (v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/site-settings', settings);
      toast({ title: 'Saved', description: 'Site backgrounds updated. Refresh the site to see changes.' });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  // ----- Hero slides -----
  const setSlide = (idx, patch) =>
    setSettings((s) => ({ ...s, heroSlides: s.heroSlides.map((sl, i) => (i === idx ? { ...sl, ...patch } : sl)) }));
  const addSlide = () => setSettings((s) => ({ ...s, heroSlides: [...s.heroSlides, { ...EMPTY_SLIDE }] }));
  const removeSlide = (idx) => setSettings((s) => ({ ...s, heroSlides: s.heroSlides.filter((_, i) => i !== idx) }));
  const moveSlide = (idx, dir) => {
    setSettings((s) => {
      const arr = [...s.heroSlides];
      const j = idx + (dir === 'up' ? -1 : 1);
      if (j < 0 || j >= arr.length) return s;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...s, heroSlides: arr };
    });
  };

  // ----- Gallery -----
  const setGalleryAt = (idx, url) =>
    setSettings((s) => ({ ...s, galleryImages: s.galleryImages.map((g, i) => (i === idx ? url : g)) }));
  const addGallery = () => setSettings((s) => ({ ...s, galleryImages: [...s.galleryImages, ''] }));
  const removeGallery = (idx) => setSettings((s) => ({ ...s, galleryImages: s.galleryImages.filter((_, i) => i !== idx) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#0F1C2E]">Site Settings</h1>
          <p className="text-gray-500 text-sm mt-1">
            Upload homepage hero slides, background images and gallery photos. Leave a section empty to keep the built-in defaults.
          </p>
        </div>
        <button onClick={save} disabled={saving} data-testid="settings-save"
                className="btn-wrappus text-sm disabled:opacity-50">
          <Save size={16} /> {saving ? 'Saving…' : 'Save All Changes'}
        </button>
      </div>

      {/* Hero slides */}
      <section className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading font-bold text-lg text-[#0F1C2E]">Homepage Hero Slides</h2>
            <p className="text-xs text-gray-500 mt-1">Full-width slider on the home page. If empty, built-in slides are used.</p>
          </div>
          <button onClick={addSlide} className="btn-wrappus text-xs" data-testid="settings-add-slide"><Plus size={14} /> Add Slide</button>
        </div>

        {settings.heroSlides.length === 0 && (
          <div className="text-sm text-gray-400 text-center py-6">No custom hero slides yet — using built-in defaults.</div>
        )}

        <div className="space-y-4">
          {settings.heroSlides.map((sl, idx) => (
            <div key={idx} className="border border-gray-100 rounded-xl p-4 bg-[#FDF3EC]/40" data-testid={`settings-slide-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0F1C2E]">
                  <GripVertical size={16} className="text-gray-400" /> Slide {idx + 1}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveSlide(idx, 'up')} disabled={idx === 0} className="p-1.5 rounded hover:bg-white disabled:opacity-30 text-sm">↑</button>
                  <button onClick={() => moveSlide(idx, 'down')} disabled={idx === settings.heroSlides.length - 1} className="p-1.5 rounded hover:bg-white disabled:opacity-30 text-sm">↓</button>
                  <button onClick={() => removeSlide(idx)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Tag (small text on top)"><input value={sl.tag || ''} onChange={(e) => setSlide(idx, { tag: e.target.value })} className="inp" placeholder="REWARDS PROGRAM" /></Field>
                <Field label="Title *"><textarea rows={2} value={sl.title} onChange={(e) => setSlide(idx, { title: e.target.value })} className="inp resize-none" placeholder="Big headline. Use \n for new line." /></Field>
                <Field label="Subtitle" full><textarea rows={2} value={sl.subtitle || ''} onChange={(e) => setSlide(idx, { subtitle: e.target.value })} className="inp resize-none" /></Field>
                <Field label="Button Label"><input value={sl.cta || ''} onChange={(e) => setSlide(idx, { cta: e.target.value })} className="inp" /></Field>
                <Field label="Button Link"><input value={sl.ctaTo || ''} onChange={(e) => setSlide(idx, { ctaTo: e.target.value })} className="inp" placeholder="/menu" /></Field>
                <Field label="Start Date (optional)">
                  <input type="date" value={sl.startAt || ''} onChange={(e) => setSlide(idx, { startAt: e.target.value })} className="inp" data-testid={`settings-slide-start-${idx}`} />
                </Field>
                <Field label="End Date (optional)">
                  <input type="date" value={sl.endAt || ''} onChange={(e) => setSlide(idx, { endAt: e.target.value })} className="inp" data-testid={`settings-slide-end-${idx}`} />
                </Field>
                <div className="md:col-span-2 -mt-2 text-xs text-gray-500">
                  {sl.startAt || sl.endAt ? (
                    <span>Schedule: {sl.startAt || 'always'} → {sl.endAt || 'always'}. Slide is hidden outside this window.</span>
                  ) : (
                    <span>Leave dates empty to always show this slide.</span>
                  )}
                </div>
                <div className="md:col-span-2">
                  <ImageUploader value={sl.image} onChange={(url) => setSlide(idx, { image: url })} label="Background Image *" hint="Wide image (1920×1080 recommended)." />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Page backgrounds */}
      <section className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <h2 className="font-heading font-bold text-lg text-[#0F1C2E] mb-4">Page Section Images</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <ImageUploader value={settings.aboutImage} onChange={setField('aboutImage')} label="About Section Image" hint="Home page — About Wrappus block." />
          <ImageUploader value={settings.hoursImage} onChange={setField('hoursImage')} label="Opening Hours Image" hint="Home page — hours block." />
          <ImageUploader value={settings.whyImage} onChange={setField('whyImage')} label="Why Choose Us Image" hint="Home page — right column of the Why block." />
          <ImageUploader value={settings.signatureBgImage} onChange={setField('signatureBgImage')} label="Signature Section Background" hint='Full-width band with "WE SERVE THE BEST WRAPS".' />
        </div>
      </section>

      {/* Per-page hero images */}
      <section className="bg-white rounded-2xl shadow-sm p-6 mb-6" data-testid="settings-page-heroes">
        <div className="mb-4">
          <h2 className="font-heading font-bold text-lg text-[#0F1C2E]">Page Hero Banners</h2>
          <p className="text-xs text-gray-500 mt-1">Wide banner shown at the top of each secondary page. Leave empty to keep the built-in gradient.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <ImageUploader value={settings.aboutHero} onChange={setField('aboutHero')} label="About Page Hero" hint="Shown at the top of /about" />
          <ImageUploader value={settings.faqsHero} onChange={setField('faqsHero')} label="FAQs Page Hero" hint="Shown at the top of /faqs" />
          <ImageUploader value={settings.contactHero} onChange={setField('contactHero')} label="Contact Page Hero" hint="Shown at the top of /contact" />
          <ImageUploader value={settings.franchisingHero} onChange={setField('franchisingHero')} label="Franchising Page Hero" hint="Shown at the top of /franchising" />
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading font-bold text-lg text-[#0F1C2E]">Home Gallery Strip</h2>
            <p className="text-xs text-gray-500 mt-1">Horizontally scrolling gallery below the About section. Add 4+ images.</p>
          </div>
          <button onClick={addGallery} className="btn-wrappus text-xs" data-testid="settings-add-gallery"><Plus size={14} /> Add Image</button>
        </div>
        {settings.galleryImages.length === 0 && (
          <div className="text-sm text-gray-400 text-center py-6">No gallery images set — using built-in defaults.</div>
        )}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {settings.galleryImages.map((g, idx) => (
            <div key={idx} className="border border-gray-100 rounded-xl p-3 relative">
              <button onClick={() => removeGallery(idx)} className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-red-50 hover:text-red-600 z-10">
                <Trash2 size={12} />
              </button>
              <ImageUploader value={g} onChange={(url) => setGalleryAt(idx, url)} label={`Image ${idx + 1}`} />
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-wrappus disabled:opacity-50">
          <Save size={16} /> {saving ? 'Saving…' : 'Save All Changes'}
        </button>
      </div>

      <style>{`.inp{width:100%;border-radius:0.75rem;border:1px solid #E5E7EB;background:white;padding:0.65rem 0.9rem;font-size:0.9rem;} .inp:focus{outline:none;border-color:#ED2E2E;box-shadow:0 0 0 3px rgba(237,46,46,0.15);} `}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">{label}</label>
      {children}
    </div>
  );
}
